import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AuthedImage from '../../components/AuthedImage';
import { openWithAuth } from '../../utils/authedResource';
import {
  Card,
  Tabs,
  Grid,
  Row,
  Col,
  Statistic,
  Table,
  Space,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Modal,
  message,
  Tag,
  Upload,
  Popconfirm,
} from 'antd';
import { PlusOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';
import { useAuth } from '../../context/AuthContext';

const { TextArea } = Input;

const RESULT_OPTIONS = [
  { value: 'PASS', label: '正常' },
  { value: 'FAIL', label: '异常' },
];

const STATUS_OPTIONS_TASK = [
  { value: 'ACTIVE', label: '启用' },
  { value: 'INACTIVE', label: '停用' },
  { value: 'ARCHIVED', label: '归档' },
];

const STATUS_OPTIONS_RECORD = [
  { value: 'OPEN', label: '未闭环' },
  { value: 'CLOSED', label: '已闭环' },
];

const FREQ_OPTIONS = [
  { value: 'ONCE', label: '单次' },
  { value: 'DAILY', label: '每日' },
  { value: 'WEEKLY', label: '每周' },
  { value: 'MONTHLY', label: '每月' },
  { value: 'CUSTOM', label: '自定义' },
];

const isProbablyImageUrl = (url, name) => {
  const u = String(url || '').toLowerCase();
  const n = String(name || '').toLowerCase();
  return /\.(png|jpg|jpeg|gif|webp|bmp|svg)(\?|#|$)/.test(u) || /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/.test(n);
};

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const formatDateOnly = (v) => {
  if (!v) return null;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
};

const formatDateTime = (v) => {
  if (!v) return null;
  if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD HH:mm:ss');
  const d = dayjs(v);
  return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : null;
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatParticipantNames = (participants) => {
  const list = Array.isArray(participants) ? participants : [];
  return list
    .map((item) => item?.participant_name || item?.name || item?.username || '')
    .filter(Boolean)
    .join('、');
};

const buildLedgerPrintHtml = (record) => {
  const checklistRows = (record?.checklist_results || []).map((item, index) => `
    <tr>
      <td>${index + 1}</td>
      <td>${escapeHtml(item.item_title || '')}</td>
      <td>${item.check_result === 'FAIL' ? '异常' : item.check_result === 'NA' ? '不适用' : '正常'}</td>
      <td>${escapeHtml(item.check_desc || '')}</td>
      <td>${escapeHtml(item.abnormal_desc || '')}</td>
    </tr>
  `).join('');

  const photoBlocks = (record?.photos || []).map((item) => `
    <div style="width: 180px; margin: 8px; border: 1px solid #d9d9d9; padding: 8px; border-radius: 6px;">
      <div style="font-size: 12px; color: #666; margin-bottom: 6px;">上传人：${escapeHtml(item.uploaded_by || '—')}</div>
      <img src="${escapeHtml(item.file_path || '')}" alt="${escapeHtml(item.file_name || '照片')}" style="width: 100%; height: 120px; object-fit: cover; border-radius: 4px;" />
      <div style="font-size: 12px; margin-top: 6px; word-break: break-all;">${escapeHtml(item.file_name || '现场照片')}</div>
    </div>
  `).join('');

  const signatureBlocks = (record?.signatures || []).map((item) => `
    <div style="width: 200px; margin: 8px; border: 1px solid #d9d9d9; padding: 8px; border-radius: 6px;">
      <div style="font-size: 12px; color: #666; margin-bottom: 6px;">签字人：${escapeHtml(item.signer_name || '—')}</div>
      <img src="${escapeHtml(item.signature_file_path || '')}" alt="${escapeHtml(item.signer_name || '签名')}" style="width: 100%; height: 100px; object-fit: contain; background: #fff; border-radius: 4px;" />
      <div style="font-size: 12px; margin-top: 6px;">签字时间：${escapeHtml(item.signed_at ? dayjs(item.signed_at).format('YYYY-MM-DD HH:mm') : '—')}</div>
    </div>
  `).join('');

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>安全检查台账打印</title>
      <style>
        body { font-family: Arial, sans-serif; color: #111; padding: 24px; }
        h1 { margin: 0 0 16px; font-size: 24px; }
        .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; margin-bottom: 20px; }
        .meta div { font-size: 14px; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #d9d9d9; padding: 8px; font-size: 13px; vertical-align: top; }
        th { background: #fafafa; }
        .section-title { margin-top: 24px; margin-bottom: 8px; font-size: 16px; font-weight: 700; }
        .flex-wrap { display: flex; flex-wrap: wrap; }
      </style>
    </head>
    <body>
      <h1>安全检查台账</h1>
      <div class="meta">
        <div>计划标题：${escapeHtml(record?.task_title || record?.title || '—')}</div>
        <div>检查时间：${escapeHtml(record?.inspection_date ? dayjs(record.inspection_date).format('YYYY-MM-DD HH:mm') : '—')}</div>
        <div>检查地点：${escapeHtml(record?.location || record?.task_location || '—')}</div>
        <div>检查结果：${record?.result === 'FAIL' ? '异常' : '正常'}</div>
        <div>检查人：${escapeHtml(record?.inspector_name || '—')}</div>
        <div>参加检查人：${escapeHtml(formatParticipantNames(record?.participants) || '—')}</div>
        <div>闭环状态：${record?.status === 'CLOSED' ? '已闭环' : '未闭环'}</div>
        <div>整改责任人：${escapeHtml(record?.rectification_responsible || '—')}</div>
      </div>
      <div class="section-title">问题描述</div>
      <div>${escapeHtml(record?.issues || '—')}</div>
      <div class="section-title">建议/措施</div>
      <div>${escapeHtml(record?.suggestions || '—')}</div>
      <div class="section-title">检查项目明细</div>
      <table>
        <thead>
          <tr>
            <th style="width: 48px;">序号</th>
            <th>检查项</th>
            <th style="width: 90px;">结果</th>
            <th>检查说明</th>
            <th>异常描述</th>
          </tr>
        </thead>
        <tbody>${checklistRows || '<tr><td colspan="5">暂无检查项</td></tr>'}</tbody>
      </table>
      <div class="section-title">现场照片</div>
      <div class="flex-wrap">${photoBlocks || '<div>暂无照片</div>'}</div>
      <div class="section-title">手写签名</div>
      <div class="flex-wrap">${signatureBlocks || '<div>暂无签名</div>'}</div>
    </body>
  </html>`;
};

const ResultTag = ({ value }) => {
  const v = String(value || '').toUpperCase();
  if (v === 'PASS') return <Tag color="green">正常</Tag>;
  if (v === 'FAIL') return <Tag color="red">异常</Tag>;
  return <Tag>—</Tag>;
};

const SafetyInspectionPage = () => {
  const auth = useAuth();
  const { user } = auth;
  const [activeKey, setActiveKey] = useState('dashboard');

  // Tab-level permission control (if any granular perms are configured for current user)
  const TAB_PERMS = useMemo(
    () => ({
      dashboard: 'hazard_safety_inspections_dashboard',
      tasks: 'hazard_safety_inspections_tasks',
      mine: 'hazard_safety_inspections_mine',
    }),
    []
  );
  const hasAnyTabPerm = useMemo(() => {
    const names = Object.values(TAB_PERMS);
    return names.some((p) => auth?.hasPermission?.([p]));
  }, [TAB_PERMS, auth]);
  const visibleTabKeys = useMemo(() => {
    const keys = ['dashboard', 'tasks', 'mine'];
    if (!hasAnyTabPerm) return keys;
    return keys.filter((k) => {
      const perm = TAB_PERMS[k];
      return !perm || !!auth?.hasPermission?.([perm]);
    });
  }, [TAB_PERMS, auth, hasAnyTabPerm]);

  useEffect(() => {
    if (!visibleTabKeys.length) return;
    if (!visibleTabKeys.includes(activeKey)) {
      setActiveKey(visibleTabKeys[0]);
    }
  }, [activeKey, visibleTabKeys]);

  const screens = Grid.useBreakpoint();
  const isMobile = !!screens.xs && !screens.sm;
  const tableScrollX = isMobile ? { x: 'max-content' } : undefined;

  // ===== Dashboard =====
  const [dashLoading, setDashLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [ledgersLoading, setLedgersLoading] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(10);
  const [ledgerKeyword, setLedgerKeyword] = useState('');
  const [ledgerTaskId, setLedgerTaskId] = useState(undefined);
  const [ledgerStatus, setLedgerStatus] = useState(undefined);
  const [ledgerResult, setLedgerResult] = useState(undefined);

  const fetchDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const resp = await api.get('/safety-inspections/dashboard');
      setDashboard(resp?.data?.data || null);
    } catch (e) {
      message.error('获取检查台账概览失败：' + (e?.response?.data?.error || e?.message));
    } finally {
      setDashLoading(false);
    }
  }, []);

  // ===== Tasks =====
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskRows, setTaskRows] = useState([]);
  const [taskTotal, setTaskTotal] = useState(0);
  const [taskPage, setTaskPage] = useState(1);
  const [taskPageSize, setTaskPageSize] = useState(10);
  const [taskKeyword, setTaskKeyword] = useState('');
  const [taskStatus, setTaskStatus] = useState(undefined);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm] = Form.useForm();
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionsLoading, setUserOptionsLoading] = useState(false);
  const [userSearchTimer, setUserSearchTimer] = useState(null);

  const mergeUserOptions = useCallback((items) => {
    setUserOptions((prev) => {
      const map = new Map((prev || []).map((item) => [Number(item.id), item]));
      (items || []).forEach((item) => {
        if (!item?.id) return;
        map.set(Number(item.id), { ...map.get(Number(item.id)), ...item });
      });
      return Array.from(map.values());
    });
  }, []);

  const fetchUserOptions = useCallback(async (keyword) => {
    setUserOptionsLoading(true);
    try {
      const resp = await api.get('/safety-inspections/user-options', {
        params: { keyword: keyword || undefined, limit: 50 },
      });
      mergeUserOptions(resp?.data?.data || []);
    } catch (e) {
      message.error('获取用户列表失败：' + (e?.response?.data?.error || e?.message));
    } finally {
      setUserOptionsLoading(false);
    }
  }, [mergeUserOptions]);

  const fetchTasks = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? taskPage;
    const nextSize = opts.pageSize ?? taskPageSize;
    setTasksLoading(true);
    try {
      const resp = await api.get('/safety-inspections/tasks', {
        params: {
          page: nextPage,
          page_size: nextSize,
          keyword: taskKeyword || undefined,
          status: taskStatus || undefined,
        },
      });
      setTaskRows(resp?.data?.data || []);
      setTaskTotal(Number(resp?.data?.total || 0));
    } catch (e) {
      message.error('获取检查计划失败：' + (e?.response?.data?.error || e?.message));
    } finally {
      setTasksLoading(false);
    }
  }, [taskKeyword, taskPage, taskPageSize, taskStatus]);

  const fetchLedgers = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? ledgerPage;
    const nextSize = opts.pageSize ?? ledgerPageSize;
    setLedgersLoading(true);
    try {
      const resp = await api.get('/safety-inspections/ledgers', {
        params: {
          page: nextPage,
          page_size: nextSize,
          keyword: ledgerKeyword || undefined,
          task_id: ledgerTaskId || undefined,
          status: ledgerStatus || undefined,
          result: ledgerResult || undefined,
        },
      });
      setLedgerRows(resp?.data?.data || []);
      setLedgerTotal(Number(resp?.data?.total || 0));
    } catch (e) {
      message.error('获取检查台账失败：' + (e?.response?.data?.error || e?.message));
    } finally {
      setLedgersLoading(false);
    }
  }, [ledgerKeyword, ledgerPage, ledgerPageSize, ledgerResult, ledgerStatus, ledgerTaskId]);

  const openCreateTask = () => {
    setEditingTask(null);
    taskForm.resetFields();
    taskForm.setFieldsValue({ status: 'ACTIVE' });
    setTaskModalOpen(true);
    fetchUserOptions('');
  };

  const openEditTask = (row) => {
    setEditingTask(row);
    taskForm.resetFields();
    taskForm.setFieldsValue({
      ...row,
      planned_date: row?.planned_date ? dayjs(row.planned_date) : null,
    });
    setTaskModalOpen(true);
    fetchUserOptions(row?.responsible_name || '');
  };

  const submitTask = async () => {
    const values = await taskForm.validateFields();
    const payload = {
      ...values,
      planned_date: formatDateOnly(values?.planned_date),
      responsible_id: values?.responsible_id ? Number(values.responsible_id) : undefined,
    };
    try {
      if (editingTask?.id) {
        await api.put(`/safety-inspections/tasks/${editingTask.id}`, payload);
        message.success('任务已更新');
      } else {
        await api.post('/safety-inspections/tasks', payload);
        message.success('任务已创建');
      }
      setTaskModalOpen(false);
      setEditingTask(null);
      fetchTasks({ page: 1 });
      fetchDashboard();
    } catch (e) {
      message.error('保存失败：' + (e?.response?.data?.error || e?.response?.data?.message || e?.message));
    }
  };

  const deleteTask = async (id) => {
    try {
      await api.delete(`/safety-inspections/tasks/${id}`);
      message.success('任务已删除');
      fetchTasks();
      fetchDashboard();
    } catch (e) {
      message.error('删除失败：' + (e?.response?.data?.error || e?.message));
    }
  };

  const updateTaskStatus = async (row, nextStatus) => {
    try {
      await api.patch(`/safety-inspections/tasks/${row.id}/status`, { status: nextStatus });
      message.success('检查计划状态已更新');
      fetchTasks();
      fetchDashboard();
      fetchLedgers({ page: 1 });
    } catch (e) {
      message.error('更新状态失败：' + (e?.response?.data?.error || e?.message));
    }
  };

  // ===== Records (Mine) =====
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordRows, setRecordRows] = useState([]);
  const [recordTotal, setRecordTotal] = useState(0);
  const [recordPage, setRecordPage] = useState(1);
  const [recordPageSize, setRecordPageSize] = useState(10);
  const [recordKeyword, setRecordKeyword] = useState('');
  const [recordStatus, setRecordStatus] = useState(undefined);
  const [recordResult, setRecordResult] = useState(undefined);

  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordForm] = Form.useForm();
  const [recordUploadList, setRecordUploadList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const primeParticipantOptions = useCallback((participants) => {
    mergeUserOptions((participants || []).filter((item) => item?.user_id).map((item) => ({
      id: Number(item.user_id),
      name: item.participant_name,
      username: item.participant_name,
      department: item.department || null,
    })));
  }, [mergeUserOptions]);

  const fetchMyRecords = useCallback(async (opts = {}) => {
    const nextPage = opts.page ?? recordPage;
    const nextSize = opts.pageSize ?? recordPageSize;
    setRecordsLoading(true);
    try {
      const resp = await api.get('/safety-inspections/records', {
        params: {
          mine: true,
          page: nextPage,
          page_size: nextSize,
          keyword: recordKeyword || undefined,
          status: recordStatus || undefined,
          result: recordResult || undefined,
        },
      });
      setRecordRows(resp?.data?.data || []);
      setRecordTotal(Number(resp?.data?.total || 0));
    } catch (e) {
      message.error('获取我的检查记录失败：' + (e?.response?.data?.error || e?.message));
    } finally {
      setRecordsLoading(false);
    }
  }, [recordKeyword, recordPage, recordPageSize, recordResult, recordStatus]);

  const openCreateRecord = (prefillTask) => {
    setEditingRecord(null);
    recordForm.resetFields();
    setRecordUploadList([]);
    primeParticipantOptions(prefillTask?.participants || []);
    recordForm.setFieldsValue({
      inspection_date: dayjs(),
      status: 'OPEN',
      result: 'PASS',
      task_id: prefillTask?.id ? Number(prefillTask.id) : undefined,
      title: prefillTask?.title || undefined,
      location: prefillTask?.location || undefined,
      participant_user_ids: (prefillTask?.participants || []).map((item) => Number(item.user_id)).filter(Boolean),
    });
    setRecordModalOpen(true);
    fetchUserOptions('');
  };

  const openEditRecord = (row) => {
    setEditingRecord(row);
    recordForm.resetFields();
    setRecordUploadList([]);
    primeParticipantOptions(row?.participants || []);
    recordForm.setFieldsValue({
      ...row,
      inspection_date: row?.inspection_date ? dayjs(row.inspection_date) : dayjs(),
      rectification_deadline: row?.rectification_deadline ? dayjs(row.rectification_deadline) : null,
      participant_user_ids: (row?.participants || []).map((item) => Number(item.user_id)).filter(Boolean),
    });
    setRecordModalOpen(true);
    fetchUserOptions('');
  };

  const handleRecordTaskChange = (taskId) => {
    const task = (taskRows || []).find((item) => Number(item.id) === Number(taskId));
    if (!task) return;
    primeParticipantOptions(task?.participants || []);
    recordForm.setFieldsValue({
      title: task.title || undefined,
      location: task.location || undefined,
      participant_user_ids: (task.participants || []).map((item) => Number(item.user_id)).filter(Boolean),
    });
  };

  const printLedger = async (recordId) => {
    try {
      const resp = await api.get(`/safety-inspections/ledgers/${recordId}/print`);
      const data = resp?.data?.data;
      if (!data) {
        message.error('未获取到打印数据');
        return;
      }
      const win = window.open('', '_blank', 'noopener,noreferrer');
      if (!win) {
        message.error('浏览器拦截了打印窗口，请允许弹窗后重试');
        return;
      }
      win.document.open();
      win.document.write(buildLedgerPrintHtml(data));
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    } catch (e) {
      message.error('打印失败：' + (e?.response?.data?.error || e?.message));
    }
  };

  const handlePreview = async (file) => {
    const url = file?.url || file?.thumbUrl || '';
    const type = String(file?.type || file?.originFileObj?.type || '').toLowerCase();
    if (url && !(type.startsWith('image/') || isProbablyImageUrl(url, file?.name))) {
      await openWithAuth(url);
      return;
    }
    if (url) {
      setPreviewImage(url);
      setPreviewOpen(true);
      return;
    }
    if (file?.originFileObj) {
      try {
        const base64 = await getBase64(file.originFileObj);
        setPreviewImage(base64);
        setPreviewOpen(true);
      } catch (e) {
        message.error('预览失败');
      }
    }
  };

  const uploadRecordAttachment = async (recordId, originFileObj) => {
    const fd = new FormData();
    fd.append('file', originFileObj);
    await api.post(`/safety-inspections/records/${recordId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const submitRecord = async () => {
    const values = await recordForm.validateFields();
    const payload = {
      task_id: values?.task_id ? Number(values.task_id) : null,
      title: values?.title || null,
      location: values?.location || null,
      inspection_date: formatDateTime(values?.inspection_date) || formatDateTime(dayjs()),
      result: values?.result,
      issues: values?.issues || null,
      suggestions: values?.suggestions || null,
      rectification_responsible: values?.rectification_responsible || null,
      rectification_deadline: formatDateOnly(values?.rectification_deadline),
      status: values?.status || 'OPEN',
      remark: values?.remark || null,
      participants: (values?.participant_user_ids || []).map((userId) => {
        const numericId = Number(userId);
        const option = (userOptions || []).find((item) => Number(item.id) === numericId)
          || (editingRecord?.participants || []).find((item) => Number(item.user_id) === numericId)
          || (taskRows || []).flatMap((task) => task.participants || []).find((item) => Number(item.user_id) === numericId);
        return {
          user_id: numericId,
          participant_name: option?.participant_name || option?.name || option?.username || `用户${numericId}`,
          participant_role: option?.participant_role || '检查人',
        };
      }),
    };

    try {
      let recordId = editingRecord?.id;
      if (recordId) {
        await api.put(`/safety-inspections/records/${recordId}`, payload);
      } else {
        const resp = await api.post('/safety-inspections/records', payload);
        recordId = resp?.data?.id;
      }

      const newLocalFiles = (recordUploadList || []).filter(f => f?.originFileObj);
      const failed = [];
      for (const f of newLocalFiles) {
        try {
          await uploadRecordAttachment(recordId, f.originFileObj);
        } catch (e) {
          failed.push({ name: f?.name || '未知文件', reason: e?.response?.data?.error || e?.message || '上传失败' });
        }
      }

      if (failed.length) {
        message.warning(`记录已保存，但有 ${failed.length} 个附件上传失败`);
      } else {
        message.success('检查记录已保存');
      }

      setRecordModalOpen(false);
      setEditingRecord(null);
      setRecordUploadList([]);
      fetchMyRecords({ page: 1 });
      fetchDashboard();
      fetchLedgers({ page: 1 });
    } catch (e) {
      message.error('保存失败：' + (e?.response?.data?.error || e?.response?.data?.message || e?.message));
    }
  };

  const deleteRecord = async (id) => {
    try {
      await api.delete(`/safety-inspections/records/${id}`);
      message.success('记录已删除');
      fetchMyRecords();
      fetchDashboard();
      fetchLedgers();
    } catch (e) {
      message.error('删除失败：' + (e?.response?.data?.error || e?.message));
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      await api.delete(`/safety-inspections/attachments/${attachmentId}`);
      message.success('附件已删除');
      fetchMyRecords();
    } catch (e) {
      message.error('删除附件失败：' + (e?.response?.data?.error || e?.message));
    }
  };

  // initial load + tab-based refresh
  useEffect(() => {
    fetchDashboard();
    fetchTasks({ page: 1 });
    fetchMyRecords({ page: 1 });
    fetchLedgers({ page: 1 });
  }, [fetchDashboard, fetchLedgers, fetchMyRecords, fetchTasks]);

  // derived: active tasks for record task select
  const activeTaskOptions = useMemo(() => {
    const rows = Array.isArray(taskRows) ? taskRows : [];
    return rows
      .filter(r => String(r?.status || '').toUpperCase() === 'ACTIVE')
      .map(r => ({ label: `${r.title}${r.location ? `（${r.location}）` : ''}`, value: Number(r.id) }));
  }, [taskRows]);

  const dashboardTab = (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="检查台账概览"
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchDashboard} loading={dashLoading}>
            刷新
          </Button>
        }
      >
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="启用任务" value={dashboard?.task_active_count ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="7天内计划" value={dashboard?.task_due_7d_count ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="本月检查" value={dashboard?.record_month_count ?? 0} />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <Statistic title="本月异常" value={dashboard?.record_fail_month_count ?? 0} valueStyle={{ color: '#cf1322' }} />
          </Col>
        </Row>
      </Card>

      <Card title="最近检查记录" bodyStyle={{ paddingTop: 8 }}>
        <Table
          size="small"
          rowKey="id"
          loading={dashLoading}
          dataSource={dashboard?.recent_records || []}
          pagination={false}
          scroll={tableScrollX}
          columns={[
            { title: '时间', dataIndex: 'inspection_date', width: 170, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
            { title: '标题', dataIndex: 'title', ellipsis: true, render: (v) => v || '—' },
            { title: '地点', dataIndex: 'location', width: 160, ellipsis: true, render: (v) => v || '—', responsive: ['md'] },
            { title: '结果', dataIndex: 'result', width: 90, render: (v) => <ResultTag value={v} /> },
            { title: '检查人', dataIndex: 'inspector_name', width: 120, render: (v) => v || '—', responsive: ['md'] },
          ]}
        />
      </Card>

      <Card title="检查台账" bodyStyle={{ paddingTop: 8 }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              allowClear
              value={ledgerKeyword}
              onChange={(e) => setLedgerKeyword(e.target.value)}
              placeholder="搜索：标题/地点/检查人"
              onPressEnter={() => fetchLedgers({ page: 1 })}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              allowClear
              style={{ width: '100%' }}
              value={ledgerTaskId}
              onChange={setLedgerTaskId}
              placeholder="检查计划"
              options={activeTaskOptions}
            />
          </Col>
          <Col xs={12} sm={12} md={4}>
            <Select allowClear style={{ width: '100%' }} value={ledgerResult} onChange={setLedgerResult} placeholder="结果" options={RESULT_OPTIONS} />
          </Col>
          <Col xs={12} sm={12} md={4}>
            <Select allowClear style={{ width: '100%' }} value={ledgerStatus} onChange={setLedgerStatus} placeholder="状态" options={STATUS_OPTIONS_RECORD} />
          </Col>
          <Col xs={24} sm={12} md={3}>
            <Button onClick={() => fetchLedgers({ page: 1 })} loading={ledgersLoading} block>
              查询
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          size="small"
          loading={ledgersLoading}
          dataSource={ledgerRows}
          scroll={tableScrollX}
          pagination={{
            current: ledgerPage,
            pageSize: ledgerPageSize,
            total: ledgerTotal,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setLedgerPage(p);
              setLedgerPageSize(ps);
              fetchLedgers({ page: p, pageSize: ps });
            },
          }}
          columns={[
            { title: '检查时间', dataIndex: 'inspection_date', width: 170, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
            { title: '计划/标题', dataIndex: 'task_title', ellipsis: true, render: (v, r) => v || r.title || '—' },
            { title: '地点', dataIndex: 'location', width: 160, ellipsis: true, render: (v) => v || '—', responsive: ['md'] },
            { title: '检查人', dataIndex: 'inspector_name', width: 120, render: (v) => v || '—', responsive: ['md'] },
            { title: '异常项', dataIndex: 'checklist_fail_count', width: 90, render: (v) => Number(v || 0) },
            { title: '照片', dataIndex: 'photo_count', width: 70, render: (v) => Number(v || 0) },
            { title: '签名', dataIndex: 'signature_count', width: 70, render: (v) => Number(v || 0) },
            { title: '结果', dataIndex: 'result', width: 90, render: (v) => <ResultTag value={v} /> },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v) => {
                const s = String(v || '').toUpperCase();
                if (s === 'CLOSED') return <Tag color="green">已闭环</Tag>;
                if (s === 'OPEN') return <Tag color="orange">未闭环</Tag>;
                return <Tag>{v || '—'}</Tag>;
              },
            },
            {
              title: '操作',
              key: 'action',
              width: 100,
              render: (_, r) => (
                <Button size="small" type="link" onClick={() => printLedger(r.id)}>
                  打印
                </Button>
              ),
            },
          ]}
        />
      </Card>
    </Space>
  );

  const tasksTab = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card
        title="检查计划"
        extra={
          <Space wrap>
            <Button size={isMobile ? 'small' : undefined} icon={<ReloadOutlined />} onClick={() => fetchTasks({ page: 1 })} loading={tasksLoading}>
              刷新
            </Button>
            <Button size={isMobile ? 'small' : undefined} type="primary" icon={<PlusOutlined />} onClick={openCreateTask}>
              新建计划
            </Button>
          </Space>
        }
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              allowClear
              value={taskKeyword}
              onChange={(e) => setTaskKeyword(e.target.value)}
              placeholder="搜索：标题/地点/频次/责任人"
              onPressEnter={() => fetchTasks({ page: 1 })}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              style={{ width: '100%' }}
              value={taskStatus}
              onChange={(v) => setTaskStatus(v)}
              placeholder="状态"
              options={STATUS_OPTIONS_TASK}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button onClick={() => fetchTasks({ page: 1 })} loading={tasksLoading} block>
              查询
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          size="small"
          loading={tasksLoading}
          dataSource={taskRows}
          scroll={tableScrollX}
          pagination={{
            current: taskPage,
            pageSize: taskPageSize,
            total: taskTotal,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setTaskPage(p);
              setTaskPageSize(ps);
              fetchTasks({ page: p, pageSize: ps });
            },
          }}
          columns={[
            { title: '标题', dataIndex: 'title', ellipsis: true },
            { title: '地点', dataIndex: 'location', width: 160, ellipsis: true, render: (v) => v || '—', responsive: ['md'] },
            {
              title: '频次',
              dataIndex: 'frequency_type',
              width: 120,
              responsive: ['md'],
              render: (v, r) => {
                const label = FREQ_OPTIONS.find(o => o.value === v)?.label || v || '—';
                return r?.frequency_value ? `${label} · ${r.frequency_value}` : label;
              },
            },
            { title: '计划日期', dataIndex: 'planned_date', width: 120, render: (v) => (v ? String(v).slice(0, 10) : '—'), responsive: ['md'] },
            { title: '责任人', dataIndex: 'responsible_name', width: 120, render: (v) => v || '—', responsive: ['md'] },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v) => {
                const s = String(v || '').toUpperCase();
                if (s === 'ACTIVE') return <Tag color="green">启用</Tag>;
                if (s === 'INACTIVE') return <Tag>停用</Tag>;
                if (s === 'ARCHIVED') return <Tag color="gold">归档</Tag>;
                return <Tag>{v || '—'}</Tag>;
              },
            },
            {
              title: '操作',
              key: 'action',
              width: 220,
              render: (_, r) => (
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                  <Button size="small" type={isMobile ? 'primary' : 'default'} block={isMobile} onClick={() => openCreateRecord(r)}>
                    去检查
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    onClick={() => updateTaskStatus(r, String(r.status || '').toUpperCase() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  >
                    {String(r.status || '').toUpperCase() === 'ACTIVE' ? '停用' : '启用'}
                  </Button>
                  <Button size="small" type="link" onClick={() => openEditTask(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确认删除该任务？" onConfirm={() => deleteTask(r.id)}>
                    <Button size="small" type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingTask?.id ? '编辑检查计划' : '新建检查计划'}
        open={taskModalOpen}
        onCancel={() => setTaskModalOpen(false)}
        onOk={submitTask}
        okText="保存"
        destroyOnClose
      >
        <Form form={taskForm} layout="vertical" preserve={false}>
          <Form.Item name="title" label="任务标题" rules={[{ required: true, message: '请输入任务标题' }]}>
            <Input placeholder="例如：车间安全检查" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="location" label="检查地点">
                <Input placeholder="例如：一号车间" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="planned_date" label="计划日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="frequency_type" label="频次类型">
                <Select allowClear options={FREQ_OPTIONS} placeholder="可选" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="frequency_value" label="频次说明">
                <Input placeholder="例如：每周一 / 每月1号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="responsible_id" label="责任人（可搜索）">
                <Select
                  allowClear
                  showSearch
                  placeholder="输入姓名/账号搜索"
                  filterOption={false}
                  loading={userOptionsLoading}
                  options={(userOptions || []).map(u => ({
                    value: Number(u.id),
                    label: `${u.name || u.username}${u.department ? `（${u.department}）` : u.username ? `（${u.username}）` : ''}`,
                  }))}
                  onSearch={(kw) => {
                    if (userSearchTimer) clearTimeout(userSearchTimer);
                    const t = setTimeout(() => fetchUserOptions(kw), 250);
                    setUserSearchTimer(t);
                  }}
                  onChange={(val) => {
                    if (!val) {
                      taskForm.setFieldsValue({ responsible_name: undefined });
                      return;
                    }
                    const hit = (userOptions || []).find(u => Number(u.id) === Number(val));
                    if (hit) taskForm.setFieldsValue({ responsible_name: hit.name || hit.username });
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="status" label="状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={STATUS_OPTIONS_TASK} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="responsible_name" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="remark" label="备注">
            <TextArea rows={3} placeholder="可选" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );

  const mineTab = (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Card
        title={`检查实施${user?.name ? `（${user.name}）` : ''}`}
        extra={
          <Space wrap>
            <Button size={isMobile ? 'small' : undefined} icon={<ReloadOutlined />} onClick={() => fetchMyRecords({ page: 1 })} loading={recordsLoading}>
              刷新
            </Button>
            <Button size={isMobile ? 'small' : undefined} type="primary" icon={<PlusOutlined />} onClick={() => openCreateRecord(null)}>
              发起检查
            </Button>
          </Space>
        }
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              allowClear
              value={recordKeyword}
              onChange={(e) => setRecordKeyword(e.target.value)}
              placeholder="搜索：标题/地点/问题/检查人"
              onPressEnter={() => fetchMyRecords({ page: 1 })}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select allowClear style={{ width: '100%' }} value={recordResult} onChange={setRecordResult} placeholder="结果" options={RESULT_OPTIONS} />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select allowClear style={{ width: '100%' }} value={recordStatus} onChange={setRecordStatus} placeholder="状态" options={STATUS_OPTIONS_RECORD} />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button onClick={() => fetchMyRecords({ page: 1 })} loading={recordsLoading} block>
              查询
            </Button>
          </Col>
        </Row>

        <Table
          rowKey="id"
          size="small"
          loading={recordsLoading}
          dataSource={recordRows}
          scroll={tableScrollX}
          pagination={{
            current: recordPage,
            pageSize: recordPageSize,
            total: recordTotal,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setRecordPage(p);
              setRecordPageSize(ps);
              fetchMyRecords({ page: p, pageSize: ps });
            },
          }}
          expandable={{
            expandedRowRender: (r) => {
              const atts = Array.isArray(r?.attachments) ? r.attachments : [];
              return (
                <div style={{ padding: 8 }}>
                  <div style={{ marginBottom: 6, color: 'rgba(0,0,0,0.65)' }}>问题描述：</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{r?.issues || '—'}</div>
                  <div style={{ marginTop: 10, marginBottom: 6, color: 'rgba(0,0,0,0.65)' }}>附件：</div>
                  {!atts.length ? (
                    <div style={{ color: 'rgba(0,0,0,0.45)' }}>无</div>
                  ) : (
                    <Space wrap>
                      {atts.map(a => (
                        <Tag
                          key={a.id}
                          style={{ maxWidth: isMobile ? 220 : 360, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <button
                            type="button"
                            onClick={() => openWithAuth(a.file_path)}
                            style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1677ff' }}
                          >
                            {a.file_name || '附件'}
                          </button>
                          <Popconfirm title="删除该附件？" onConfirm={() => deleteAttachment(a.id)}>
                            <Button type="link" danger size="small" style={{ padding: 0, height: 'auto' }}>
                              删除
                            </Button>
                          </Popconfirm>
                        </Tag>
                      ))}
                    </Space>
                  )}
                </div>
              );
            },
          }}
          columns={[
            { title: '时间', dataIndex: 'inspection_date', width: 170, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
            { title: '标题', dataIndex: 'title', ellipsis: true, render: (v) => v || '—' },
            { title: '地点', dataIndex: 'location', width: 160, ellipsis: true, render: (v) => v || '—', responsive: ['md'] },
            { title: '结果', dataIndex: 'result', width: 90, render: (v) => <ResultTag value={v} /> },
            {
              title: '状态',
              dataIndex: 'status',
              width: 90,
              render: (v) => {
                const s = String(v || '').toUpperCase();
                if (s === 'CLOSED') return <Tag color="green">已闭环</Tag>;
                if (s === 'OPEN') return <Tag color="orange">未闭环</Tag>;
                return <Tag>{v || '—'}</Tag>;
              },
            },
            {
              title: '操作',
              key: 'action',
              width: 180,
              render: (_, r) => (
                <Space direction={isMobile ? 'vertical' : 'horizontal'}>
                  <Button size="small" type="link" onClick={() => openEditRecord(r)}>
                    编辑
                  </Button>
                  <Popconfirm title="确认删除该记录？" onConfirm={() => deleteRecord(r.id)}>
                    <Button size="small" type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editingRecord?.id ? '编辑检查实施' : '新增检查实施'}
        open={recordModalOpen}
        onCancel={() => setRecordModalOpen(false)}
        onOk={submitRecord}
        okText="保存"
        width={720}
        destroyOnClose
      >
        <Form form={recordForm} layout="vertical" preserve={false}>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="task_id" label="关联任务">
                <Select allowClear options={activeTaskOptions} placeholder="可选" onChange={handleRecordTaskChange} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="inspection_date" label="检查时间" rules={[{ required: true, message: '请选择检查时间' }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="title" label="标题">
                <Input placeholder="可选（不填则从任务带入）" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location" label="地点">
                <Input placeholder="可选（不填则从任务带入）" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="result" label="检查结果" rules={[{ required: true, message: '请选择结果' }]}>
                <Select options={RESULT_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="status" label="闭环状态" rules={[{ required: true, message: '请选择状态' }]}>
                <Select options={STATUS_OPTIONS_RECORD} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="issues" label="问题描述">
            <TextArea rows={3} placeholder="异常时建议填写" />
          </Form.Item>
          <Form.Item name="suggestions" label="建议/措施">
            <TextArea rows={2} placeholder="可选" />
          </Form.Item>

          <Form.Item name="participant_user_ids" label="参加检查人">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="输入部门/姓名/账号检索并多选"
              filterOption={false}
              loading={userOptionsLoading}
              options={(userOptions || []).map((u) => ({
                value: Number(u.id),
                label: `${u.name || u.username}${u.department ? `（${u.department}）` : ''}`,
              }))}
              onSearch={(kw) => {
                if (userSearchTimer) clearTimeout(userSearchTimer);
                const t = setTimeout(() => fetchUserOptions(kw), 250);
                setUserSearchTimer(t);
              }}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="rectification_responsible" label="整改责任人">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="rectification_deadline" label="整改期限">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="可选" />
          </Form.Item>

          <Form.Item label="附件（保存后上传）">
            <Upload
              multiple
              fileList={recordUploadList}
              onChange={({ fileList }) => setRecordUploadList(fileList)}
              onPreview={handlePreview}
              beforeUpload={() => false}
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 8 }}>
              说明：附件会在“保存”成功后逐个上传，失败不影响主记录。
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <Modal open={previewOpen} footer={null} onCancel={() => setPreviewOpen(false)} title="预览">
        <AuthedImage alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </Space>
  );

  const tabItems = [
    { key: 'dashboard', label: '检查台账', children: dashboardTab },
    { key: 'tasks', label: '检查计划', children: tasksTab },
    { key: 'mine', label: '检查实施', children: mineTab },
  ].filter((t) => visibleTabKeys.includes(t.key));

  return (
    <div style={{ padding: isMobile ? 12 : 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: isMobile ? 18 : 22, lineHeight: 1.2 }}>安全检查</h1>
          <div style={{ marginTop: 6, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
            覆盖：检查计划 / 检查实施 / 检查台账
          </div>
        </div>
      </div>
      <div style={{ height: 16 }} />
      {tabItems.length ? (
        <Tabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
      ) : (
        <div style={{ color: 'rgba(0,0,0,0.45)' }}>暂无权限查看该页面内容（请在角色权限中勾选对应 Tab 权限）。</div>
      )}
    </div>
  );
};

export default SafetyInspectionPage;
